import styled from 'styled-components';
import { colors, fonts, medias } from 'assets/styled/tokens';
import { Select } from 'components/Form/subcomponents/Select';
import { Input as InputBase } from 'components/Form/subcomponents/Input';
import { MaskedInput as MaskedInputBase } from 'components/Form/subcomponents/MaskedInput';

// Fonte de títulos conforme Figma (Heading 1/2). O token fonts.family.title é Blinker,
// mas o design usa Spline Sans, então fica hardcoded como no mockup aprovado (AberturaChamado).
const titleFont = `'Spline Sans', sans-serif`;
// Não há token para o cinza secundário (#7F746A / grayscale-700 do Figma); usa-se o hex
// como no mockup aprovado.
const secondaryText = '#7f746a';

export const Page = styled.div<{ $isSuccess?: boolean }>`
  min-height: 100vh;
  background: ${({ $isSuccess }) => ($isSuccess ? colors.white : colors.pampas)};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 48px;
`;

export const FormCard = styled.div`
  width: 100%;
  max-width: 560px;
  background: ${colors.white};
  border-radius: 8px;
  padding: 32px 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  @media (min-width: ${medias.desktopSm}) {
    padding: 40px 48px;
  }
`;

export const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

// Tela de sucesso: card branco com borda sutil (logo fica fora/acima dele).
export const SuccessCard = styled.div`
  width: 100%;
  max-width: 560px;
  background: ${colors.white};
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  padding: 32px 24px;

  @media (min-width: ${medias.desktopSm}) {
    padding: 40px 48px;
  }
`;

export const LogoImage = styled.img`
  max-height: 56px;
  max-width: 200px;
  object-fit: contain;
`;

export const PageTitle = styled.h1`
  font-family: ${titleFont};
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: ${colors.carbon};
  margin: 0 0 8px;
`;

export const SupportText = styled.p`
  font-family: ${fonts.family.text};
  font-size: 16px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
  margin: 0 0 24px;
  line-height: 24px;
`;

export const SectionTitle = styled.h2`
  font-family: ${titleFont};
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${colors.carbon};
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.gray200};
`;

export const Field = styled.div`
  margin-bottom: 16px;
`;

export const FieldLabel = styled.label`
  display: block;
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: ${colors.carbon};
  margin-bottom: 6px;
`;

export const RequiredMark = styled.span`
  color: ${colors.orange600};
`;

export const FieldSubtitle = styled.span`
  font-weight: 400;
  letter-spacing: 0.3px;
  color: ${secondaryText};
`;

// Os componentes compartilhados não têm um tamanho de 40px (medium=32, large=48),
// então a altura do Figma (40px) é forçada localmente, sem alterar os componentes globais.
export const StyledSelect = styled(Select)`
  > input {
    height: 40px;
  }
`;

export const StyledInput = styled(InputBase)`
  input& {
    height: 40px;
  }
`;

export const StyledMaskedInput = styled(MaskedInputBase)`
  input& {
    height: 40px;
  }
`;

export const ErrorText = styled.span`
  display: block;
  font-family: ${fonts.family.text};
  font-size: 12px;
  letter-spacing: 0.3px;
  color: ${colors.cedar};
  margin-top: 4px;
`;

export const LockedFieldText = styled.div`
  width: 100%;
  min-height: 40px;
  border: 1px solid ${colors.gray200};
  border-radius: 4px;
  padding: 9px 12px;
  font-family: ${fonts.family.text};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
  background: ${colors.pampas};
  display: flex;
  align-items: center;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`;

export const TermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  margin-bottom: 8px;
`;

export const TermsCheckbox = styled.input`
  margin-top: 2px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

export const TermsText = styled.p`
  font-family: ${fonts.family.text};
  font-size: 12px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
  margin: 0;
  line-height: 16px;
`;

export const TermsLink = styled.a<{ brandColor?: string }>`
  color: ${({ brandColor }) => brandColor || colors.primary};
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
`;

export const TrackingTitle = styled.p`
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: ${colors.carbon};
  margin: 0 0 8px;
`;

export const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${fonts.family.text};
  font-size: 14px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
  margin-bottom: 8px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const PhotoActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

export const PhotoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 10px 8px;
  border: 1px solid ${colors.gray200};
  border-radius: 4px;
  background: ${colors.white};
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: ${colors.carbon};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PhotoThumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

export const PhotoThumb = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid ${colors.gray200};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PhotoThumbRemove = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const ActionButton = styled.button<{ brandColor?: string }>`
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: none;
  background: ${({ brandColor }) => brandColor || colors.primary};
  color: white;
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  margin-top: 24px;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  font-family: ${fonts.family.text};
  font-size: 14px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
  cursor: pointer;
  padding: 0;
  margin-top: 12px;
  text-decoration: underline;
  display: block;
  width: 100%;
  text-align: center;
`;

export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 0;
`;

export const SuccessIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: #b4ede3;
  margin-bottom: 24px;
`;

export const SuccessTitle = styled.h2`
  font-family: ${titleFont};
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${colors.carbon};
  margin: 0 0 12px;
`;

export const SuccessText = styled.p`
  font-family: ${fonts.family.text};
  font-size: 14px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
  margin: 0;
  line-height: 1.5;
`;

export const Footer = styled.footer`
  margin-top: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${fonts.family.text};
  font-size: 12px;
  letter-spacing: 0.3px;
  color: ${secondaryText};
`;

/* ── Modal de ocorrência duplicada ────────────────────────────────────────── */

export const DuplicateOverlay = styled.div`
  background: rgba(0, 0, 0, 0.7);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 24px 0;
  z-index: 6;
`;

export const DuplicateCard = styled.div`
  background-color: ${colors.white};
  border-radius: 8px;
  box-shadow: 0px 1px 8px 0px #0000001a;
  width: calc(100% - 24px);
  max-width: 448px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const DuplicateIconWrapper = styled.div`
  padding-bottom: 8px;
  svg {
    width: 64px;
    height: 64px;
  }
`;

export const DuplicateTitle = styled.h2`
  font-family: ${titleFont};
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${colors.carbon};
  margin: 0 0 16px;
`;

export const DuplicateInfoText = styled.p`
  color: ${secondaryText};
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.3px;
  line-height: 20px;
  margin: 0 0 8px;
  text-align: left;
  width: 100%;
  b {
    color: ${colors.carbon};
  }
`;

export const DuplicateActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: center;
  padding-top: 16px;
  width: 100%;
`;

export const DuplicateCancelButton = styled.button`
  min-height: 40px;
  padding: 10px 16px;
  border: 1px solid ${colors.gray200};
  border-radius: 4px;
  background-color: ${colors.white};
  color: ${colors.carbon};
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
`;

export const DuplicateConfirmButton = styled.button<{ brandColor?: string }>`
  min-height: 40px;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  background-color: ${({ brandColor }) => brandColor || colors.primary};
  color: ${colors.white};
  font-family: ${fonts.family.text};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
`;
